import { Switch } from '@base-ui/react/switch'
import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import { accents, brand } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'

export type ToggleSize = 'sm' | 'md'

export interface ToggleProps {
  // Controlled. Omit it (and pass `defaultChecked`) for an uncontrolled toggle.
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  // Blocks interaction (the toggle stays focusable) and shows a spinner in the
  // thumb; exposes `aria-busy`.
  loading?: boolean
  size?: ToggleSize
  id?: string
  className?: string
  style?: React.CSSProperties
  'aria-label'?: string
  'data-testid'?: string
}

const sizes = {
  md: { width: 36, height: 20, knob: 16 },
  sm: { width: 28, height: 16, knob: 12 },
} as const

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

const Track = styled(Switch.Root)<{ $size: ToggleSize }>`
  ${({ $size }) => css`
    width: ${sizes[$size].width}px;
    height: ${sizes[$size].height}px;
  `}
  display: inline-block;
  box-sizing: border-box;
  border-radius: 9999px;
  background: #d5d7da;
  position: relative;
  cursor: pointer;
  padding: 0;
  transition: background ${motion.duration.base}ms ${motion.ease};
  flex-shrink: 0;

  &[data-checked] {
    background: ${accents.reflectionBlue[500]};
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[aria-busy='true'] {
    opacity: 0.65;
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Knob = styled(Switch.Thumb)<{ $size: ToggleSize }>`
  ${({ $size }) => css`
    width: ${sizes[$size].knob}px;
    height: ${sizes[$size].knob}px;
    &[data-checked] {
      left: ${sizes[$size].width - sizes[$size].knob - 2}px;
    }
  `}
  position: absolute;
  top: 2px;
  left: 2px;
  box-sizing: border-box;
  border-radius: 50%;
  background: ${brand.white};
  transition: left ${motion.duration.base}ms ${motion.ease};
`

const Spinner = styled.span`
  position: absolute;
  inset: 20%;
  border: 2px solid ${accents.reflectionBlue[500]};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

export const Toggle = ({
  checked,
  defaultChecked,
  onChange,
  disabled,
  loading,
  size = 'md',
  id,
  className,
  style,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ToggleProps): React.ReactElement => (
  <Track
    checked={checked}
    defaultChecked={defaultChecked}
    onCheckedChange={(next) => onChange?.(next)}
    disabled={disabled}
    readOnly={loading}
    aria-busy={loading || undefined}
    $size={size}
    id={id}
    className={className}
    style={style}
    aria-label={ariaLabel}
    data-testid={testId}
  >
    <Knob $size={size}>
      {loading ? <Spinner data-toggle-spinner aria-hidden /> : null}
    </Knob>
  </Track>
)

Toggle.displayName = 'Toggle'
